import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axiosRetry from 'axios-retry'
import { environment } from '@/constants/environment'
import { HTTP_STATUS } from '@/constants'

export interface ApiResponse<T = any> {
  data: T
  message: string
  success: boolean
  errors?: Record<string, string[]>
}

export interface ApiErrorResponse {
  message: string
  success: false
  errors?: Record<string, string[]>
  status?: number
}

const apiClient = axios.create({
  baseURL: environment.api.baseUrl,
  timeout: environment.api.timeout,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/plain, */*',
  },
  withCredentials: false,
  maxRedirects: 5,
  validateStatus: status => status >= HTTP_STATUS.OK && status < HTTP_STATUS.MULTIPLE_CHOICES,
})

axiosRetry(apiClient, {
  retries: 3,
  shouldResetTimeout: true,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: AxiosError) => {
    if (axios.isCancel(error)) {
      return false
    }
    const method = (error.config?.method || '').toUpperCase()
    const url = error.config?.url || ''
    const isSessionEndpoint = url.includes('/sessions/')

    if (axiosRetry.isIdempotentRequestError(error) && (method === 'GET' || !isSessionEndpoint)) {
      return true
    }
    if (
      error.response &&
      method === 'POST' &&
      [500, 501, 502, 503].includes(error.response.status || 0) &&
      !isSessionEndpoint
    ) {
      return true
    }
    if (method === 'GET' && axiosRetry.isNetworkError(error)) {
      return true
    }
    return false
  },
})

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token =
      localStorage.getItem('auth_token') || localStorage.getItem('access_token') || sessionStorage.getItem('auth_token')

    if (token) {
      config.headers.Authorization = `Token ${token}`
    }

    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    if (!error.response) {
      return Promise.reject({
        type: 'network',
        message: error.code === 'ECONNABORTED' ? 'Request timeout' : 'Network error',
        originalError: error,
      })
    }

    const { status, data } = error.response

    if (status === HTTP_STATUS.UNAUTHORIZED) {
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/logout')) {
        window.dispatchEvent(new CustomEvent('session-expired'))
      }
    } else if (
      status === HTTP_STATUS.INTERNAL_SERVER_ERROR ||
      status === HTTP_STATUS.BAD_GATEWAY ||
      status === HTTP_STATUS.SERVICE_UNAVAILABLE ||
      status === HTTP_STATUS.GATEWAY_TIMEOUT
    ) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('critical-error', {
            detail: { status, message: data?.message || `HTTP ${status} Error` },
          })
        )
      }
    }

    return Promise.reject({
      type: 'api',
      status,
      message: data?.message || `HTTP ${status} Error`,
      errors: data?.errors,
      response: error.response,
      originalError: error,
    })
  }
)

export const setAuthToken = (token: string): void => {
  localStorage.setItem('auth_token', token)
}

export const removeAuthToken = (): void => {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  // Targeted removal — sessionStorage.clear() previously wiped checkout forms
  // and any third-party scratch keys. Each consumer owns its own cleanup.
  sessionStorage.removeItem('auth_token')
  sessionStorage.removeItem('checkout-customer')
  sessionStorage.removeItem('checkout-card')
  sessionStorage.removeItem('orderDetail')
}

export const getAuthToken = (): string | null => {
  return (
    localStorage.getItem('auth_token') || localStorage.getItem('access_token') || sessionStorage.getItem('auth_token')
  )
}

export const updateApiConfig = (config: Partial<typeof environment.api>): void => {
  if (config.baseUrl) {
    apiClient.defaults.baseURL = config.baseUrl
  }
  if (config.timeout) {
    apiClient.defaults.timeout = config.timeout
  }
}

export { apiClient }
export default apiClient
